import { DOMHelper } from 'core/DOMHelper';
import DelayHelper from 'utils/timers';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import { HeaderType, ProjectMediaDeps } from './project.types';

/**
 * Fullscreen header media for the project section: the video/image switch
 * when a project is queued, header video play/pause + scroll visibility and
 * sizing the header to match the Pixi work tile. The SiteTransitions show /
 * hide tweens for the header video stay in ProjectController.
 */
export class ProjectMedia {
	private _dom: DOMHelper;
	private _deps: ProjectMediaDeps;

	public headerType: HeaderType = HeaderType.IMAGE;
	public headerVideoPlaying: boolean = true;
	//NOTE (pre-existing): written from many code paths but never read —
	//previously an undeclared property on ProjectController.
	public headerSized: boolean;

	public $headerVideo: HTMLVideoElement;
	public $headerImage: HTMLImageElement;
	public $headerTarget: HTMLElement;
	public $headerVideoContainer: HTMLElement;
	public $headerVideoOverlay: HTMLElement;

	constructor(dom: DOMHelper, deps: ProjectMediaDeps) {
		this._dom = dom;
		this._deps = deps;
	}

	/** Query the header media element refs */
	public queryRefs(container: Element): void {
		//Main full screen header video
		this.$headerVideo = container.querySelector('.project__video');
		this.$headerVideoContainer = container.querySelector('.project__video__container');
		this.$headerVideoOverlay = container.querySelector('.project__video__overlay');
		this.$headerImage = container.querySelector('.project__header_image');
	}

	/** Media half of the queued-project switch: swap the header between video and image */
	public queProjectMedia(slug: string): void {
		let remove = this.headerType === HeaderType.IMAGE ? 'video' : 'image';
		this._dom.addClass(this.$headerVideoContainer, `-${this.headerType}`);
		this._dom.removeClass(this.$headerVideoContainer, `-${remove}`);

		if (this.headerType == HeaderType.VIDEO) {
			let source = this.$headerVideo.querySelector('source');
			source.setAttribute('src', `/video/${slug}.mp4`);
			DelayHelper.setTimeout(() => {
				this.$headerVideo.load();
			}, 200);

			this.$headerVideo.style.display = 'inline';
			this.$headerImage.style.display = 'none';
			this.$headerTarget = this.$headerVideo;
			return;
		}

		this.$headerVideo.style.display = 'none';
		this.$headerImage.style.display = 'inline';
		this.$headerImage.setAttribute('src', `/img/project/header/${slug}.jpg`);
		this.$headerTarget = this.$headerImage;
	}

	//Playback
	//-------------------------------------------------------------------------------------

	/** Restart playback from zero (video headers only) */
	public playHeaderVideoFromStart(): void {
		if (this.headerType === HeaderType.VIDEO) {
			this.$headerVideo.currentTime = 0;
			this.$headerVideo.play();
		}
	}

	/** Pause when transiting back to work (video headers only) */
	public pauseHeaderVideoIfVideo(): void {
		if (this.headerType === HeaderType.VIDEO) {
			this.$headerVideo.pause();
		}
	}

	/** Pause after freeze regardless of header type */
	public pauseHeaderVideo(): void {
		if (this.$headerVideo) {
			this.$headerVideo.pause();
		}
	}

	/** Pause / play the header video as it scrolls out of / into view */
	public setHeaderVideoVisible(): void {
		if (this.headerType === HeaderType.IMAGE) {
			return;
		}

		if (this.headerVideoPlaying && this._deps.getScroll() > this._deps.getInnerHeight()) {
			this.headerVideoPlaying = false;
			this.$headerVideo.pause();
			this.$headerVideo.style.visibility = 'hidden';
		} else if (!this.headerVideoPlaying && this._deps.getScroll() <= this._deps.getInnerHeight()) {
			this.$headerVideo.play();
			this.$headerVideo.style.visibility = 'visible';
			this.headerVideoPlaying = true;
		}
	}

	//Sizing
	//-------------------------------------------------------------------------------------

	/** Match Header video to the PIXI tile */
	public matchHeaderToPixi(): void {
		if (!this.$headerTarget) {
			return;
		}

		//1.) Get the size from the active tile.
		//Then just match width. The position is sorted via CSS transfrom and the

		//If there no valid rect then project has probably been displayed from a deeplink:
		//TODO replace 'video sizes';

		let containerSize = ProjectHeaderSizes.headerSize();
		//Bare call kept for side-effect parity (result was never used):
		ProjectHeaderSizes.videoSize();

		(document.querySelector('.project__video__container') as HTMLElement).style.height =
			`${containerSize.height - 1}px`;

		//If on mobile then header video is 100% height + width
		//Otherwise the spacer will be set to a fixed aspect ratio based on width (so it won't jank on mobile resize)
		if (window.innerWidth >= 900) {
		}

		this.headerSized = true;
	}
}
